import { AUTH_ROUTES } from '../components/authRoutes';
import { socialCallbackUrl } from './socialAuthOrigin';

const SOCIAL_RESULT_EVENT = 'VIDYA_KASAM_SOCIAL_AUTH_RESULT';
const SOCIAL_ERROR_EVENT = 'VIDYA_KASAM_SOCIAL_AUTH_ERROR';
const DEFAULT_POPUP_WIDTH = 520;
const DEFAULT_POPUP_HEIGHT = 680;

function buildState(provider) {
  return window.btoa(
    JSON.stringify({
      provider,
      nonce: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    })
  );
}

function parseState(rawState) {
  if (!rawState) {
    return {};
  }

  try {
    return JSON.parse(window.atob(rawState));
  } catch (error) {
    return {};
  }
}

function buildCallbackUrl(provider) {
  const configuredCallback = (provider === 'apple' && process.env.REACT_APP_APPLE_REDIRECT_URI) ||
    process.env.REACT_APP_SOCIAL_REDIRECT_URI || AUTH_ROUTES.socialCallback;
  return socialCallbackUrl(provider, window.location.origin, configuredCallback);
}

function buildPopupFeatures(width = DEFAULT_POPUP_WIDTH, height = DEFAULT_POPUP_HEIGHT) {
  const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
  const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);

  return [
    'toolbar=no',
    'location=yes',
    'status=no',
    'menubar=no',
    'scrollbars=yes',
    'resizable=yes',
    `width=${Math.round(width)}`,
    `height=${Math.round(height)}`,
    `left=${Math.round(left)}`,
    `top=${Math.round(top)}`,
  ].join(',');
}

function waitForPopupResult(popupWindow, expectedProvider) {
  return new Promise((resolve, reject) => {
    if (!popupWindow) {
      reject(new Error('Popup could not be opened. Please allow popups and try again.'));
      return;
    }

    let timeoutId;
    let closeCheckId;

    const cleanup = () => {
      window.removeEventListener('message', handleMessage);
      window.clearTimeout(timeoutId);
      window.clearInterval(closeCheckId);
    };

    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      const { data } = event;
      if (!data || data.provider !== expectedProvider) {
        return;
      }

      if (data.type === SOCIAL_RESULT_EVENT) {
        cleanup();
        resolve(data.profile);
        return;
      }

      if (data.type === SOCIAL_ERROR_EVENT) {
        cleanup();
        reject(new Error(data.message || 'Social login could not be completed.'));
      }
    };

    window.addEventListener('message', handleMessage);

    closeCheckId = window.setInterval(() => {
      if (!popupWindow.closed) {
        return;
      }

      cleanup();
      reject(new Error('Social login was cancelled before completion.'));
    }, 500);

    timeoutId = window.setTimeout(() => {
      cleanup();
      if (!popupWindow.closed) {
        popupWindow.close();
      }
      reject(new Error('Social login timed out. Please try again.'));
    }, 120000);
  });
}

function decodeJwt(token) {
  const tokenParts = token.split('.');
  if (tokenParts.length < 2) {
    throw new Error('Invalid token response.');
  }

  const payload = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
  const decoded = window.atob(payload.padEnd(Math.ceil(payload.length / 4) * 4, '='));
  return JSON.parse(decoded);
}

async function fetchJson(url) {
  const response = await fetch(url, {
    credentials: 'omit',
  });

  if (!response.ok) {
    throw new Error('Unable to verify social profile.');
  }

  return response.json();
}

function buildGoogleUrl() {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  if (!clientId) {
    throw new Error('Google login is not configured yet. Add REACT_APP_GOOGLE_CLIENT_ID.');
  }

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: buildCallbackUrl('google'),
    response_type: 'token',
    scope: 'openid email profile',
    include_granted_scopes: 'true',
    prompt: 'select_account',
    state: buildState('google'),
  });

  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

function buildFacebookUrl() {
  const appId = process.env.REACT_APP_FACEBOOK_APP_ID;
  if (!appId) {
    throw new Error('Facebook login is not configured yet. Add REACT_APP_FACEBOOK_APP_ID.');
  }

  const params = new URLSearchParams({
    client_id: appId,
    redirect_uri: buildCallbackUrl('facebook'),
    response_type: 'token',
    scope: 'public_profile,email',
    state: buildState('facebook'),
  });

  return `https://www.facebook.com/v20.0/dialog/oauth?${params.toString()}`;
}

function loadScript(scriptId, scriptSrc) {
  return new Promise((resolve, reject) => {
    const existingScript = document.getElementById(scriptId);

    if (existingScript) {
      existingScript.addEventListener('load', resolve, { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Unable to load the social login SDK.')), { once: true });

      if (existingScript.dataset.loaded === 'true') {
        resolve();
      }
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = scriptSrc;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      script.dataset.loaded = 'true';
      resolve();
    };
    script.onerror = () => reject(new Error('Unable to load the social login SDK.'));
    document.head.appendChild(script);
  });
}

async function signInWithApple() {
  const clientId = process.env.REACT_APP_APPLE_CLIENT_ID;
  const redirectUri = buildCallbackUrl('apple');

  if (!clientId) {
    throw new Error('Apple login is not configured yet. Add REACT_APP_APPLE_CLIENT_ID.');
  }

  await loadScript(
    'apple-signin-sdk',
    'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'
  );

  if (!window.AppleID?.auth) {
    throw new Error('Apple login SDK is unavailable right now.');
  }

  window.AppleID.auth.init({
    clientId,
    scope: 'name email',
    redirectURI: redirectUri,
    usePopup: true,
  });

  const response = await window.AppleID.auth.signIn();
  const claims = decodeJwt(response.authorization.id_token);

  return {
    provider: 'apple',
    socialId: claims.sub,
    email: claims.email || '',
    name: [response.user?.name?.firstName, response.user?.name?.lastName].filter(Boolean).join(' ').trim(),
  };
}

export async function beginSocialLogin(provider) {
  if (provider === 'google') {
    const popupWindow = window.open(buildGoogleUrl(), 'google-login', buildPopupFeatures());
    return waitForPopupResult(popupWindow, 'google');
  }

  if (provider === 'facebook') {
    const popupWindow = window.open(buildFacebookUrl(), 'facebook-login', buildPopupFeatures());
    return waitForPopupResult(popupWindow, 'facebook');
  }

  if (provider === 'apple') {
    return signInWithApple();
  }

  throw new Error('Unsupported social login provider.');
}

export async function resolvePopupProfile(provider, currentUrl) {
  const url = new URL(currentUrl);
  const hashParams = new URLSearchParams(url.hash.startsWith('#') ? url.hash.slice(1) : url.hash);
  const queryParams = url.searchParams;
  const activeProvider = provider || queryParams.get('provider') || parseState(hashParams.get('state')).provider;

  if (!activeProvider) {
    throw new Error('Missing social login provider.');
  }

  if (queryParams.get('error') || hashParams.get('error')) {
    throw new Error(queryParams.get('error_description') || hashParams.get('error_description') || 'Social login was denied.');
  }

  if (activeProvider === 'google') {
    const accessToken = hashParams.get('access_token');
    if (!accessToken) {
      throw new Error('Google login did not return an access token.');
    }

    const profile = await fetchJson(
      `https://www.googleapis.com/oauth2/v3/userinfo?access_token=${encodeURIComponent(accessToken)}`
    );

    return {
      provider: 'google',
      socialId: profile.sub,
      email: profile.email || '',
      name: profile.name || '',
    };
  }

  if (activeProvider === 'facebook') {
    const accessToken = hashParams.get('access_token');
    if (!accessToken) {
      throw new Error('Facebook login did not return an access token.');
    }

    const profile = await fetchJson(
      `https://graph.facebook.com/me?fields=id,name,email&access_token=${encodeURIComponent(accessToken)}`
    );

    return {
      provider: 'facebook',
      socialId: profile.id,
      email: profile.email || '',
      name: profile.name || '',
    };
  }

  if (activeProvider === 'apple') {
    const idToken = queryParams.get('id_token');
    if (!idToken) {
      throw new Error('Apple login did not return an identity token.');
    }

    const claims = decodeJwt(idToken);
    return {
      provider: 'apple',
      socialId: claims.sub,
      email: claims.email || '',
      name: '',
    };
  }

  throw new Error('Unsupported social login provider.');
}

export function postSocialResultToOpener(result) {
  if (!window.opener) {
    return;
  }

  window.opener.postMessage(
    {
      type: SOCIAL_RESULT_EVENT,
      provider: result.provider,
      profile: result,
    },
    window.location.origin
  );
}

export function postSocialErrorToOpener(provider, message) {
  if (!window.opener) {
    return;
  }

  window.opener.postMessage(
    {
      type: SOCIAL_ERROR_EVENT,
      provider,
      message,
    },
    window.location.origin
  );
}
